import React, { useState, useEffect, useMemo } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import {
  CloseButton,
  Container,
  VStack,
  HStack,
  Box,
  Flex,
  Heading,
  Text,
  Button,
  Input,
  Textarea,
  NativeSelect,
  Spinner,
  Badge,
  Alert,
  NumberInput,
  Drawer,
  IconButton,
  useDisclosure,
  Dialog,
  Portal,
  Field,
  type NumberInputValueChangeDetails,
} from '@chakra-ui/react';
import { toaster } from '@/components/ui/toaster'
import { FiSave, FiArrowLeft, FiZap, FiSliders, FiList, FiEdit3, FiX } from 'react-icons/fi'
import { MultiTurfDrawingMap, TurfLegendPanel, OverlayModeToggle } from '../components/turf'
import type { DrawnTurf, TurfMapMethods } from '../components/turf/MultiTurfDrawingMap'
import { ContactDetailModal } from '../components/contactDetail'
import { BusinessDetailModal } from '../components/business/BusinessDetailModal'
import { UnionOnly } from '../components/scoping/UnionOnly'
import { useContactLists, useContactsWithEmployment } from '../hooks/useContactList'
import { useBatchCutTurfs, useAutoGeneratePreview } from '../hooks/useTurfMap'
import { useJobPolling } from '../hooks/useJobPolling'
import { contactListApi } from '../lib/api'
import { extractApiMessage } from '../lib/apiError'
import { useErrorToast } from '../hooks/useErrorToast'
import { groupContactsByWorkplace, splitByEmploymentStatus, contactsToHouseholdMarkers } from '../components/turf/workplaceUtils'
import type { OverlayMode } from '../types/workplaceOverlay'
import type { WalkType, AutoTurfPreviewResult } from '../types/turf'
import { overlayModeToAddressMode } from '../utils/geoUtils'
import { useIsMobile } from '../hooks/useIsMobile'
import { ResponsiveModal } from '../components/shared/ResponsiveModal'
import { TurfCutImmersiveBar } from '../components/turf/TurfCutImmersiveBar'

export function TurfCutterPage() {
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const campaignId = searchParams.get('campaignId')
  const { open, onOpen, onClose } = useDisclosure()
  const autoTurfModal = useDisclosure()
  const cancelRef = React.useRef<HTMLButtonElement>(null)
  const isMobile = useIsMobile()

  const { data: lists, isLoading: listsLoading } = useContactLists()
  const batchCutMutation = useBatchCutTurfs()
  const autoGeneratePreviewMutation = useAutoGeneratePreview()

  // Filter to standalone lists only (not shadow copies)
  const standaloneLists = lists?.filter(l => !(l as any).campaignId)

  // Form state
  const [label, setLabel] = useState('')
  const [description, setDescription] = useState('')
  const [selectedListId, setSelectedListId] = useState('')
  const [walkType, setWalkType] = useState<WalkType>('Walk')
  const [drawnTurfs, setDrawnTurfs] = useState<DrawnTurf[]>([])
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false)

  // Auto-turf modal state
  const [targetTurfCount, setTargetTurfCount] = useState(5)
  const [previewJobId, setPreviewJobId] = useState<string | null>(null)
  const { data: previewJob, isTerminal: previewTerminal } = useJobPolling(previewJobId)
  const isGeneratingPreview = autoGeneratePreviewMutation.isPending || (!!previewJobId && !previewTerminal)

  // Contact detail modal state
  const [selectedContactId, setSelectedContactId] = useState<string | null>(null)
  const [isContactModalOpen, setIsContactModalOpen] = useState(false)

  // Worksite detail modal state
  const [selectedWorksiteId, setSelectedWorksiteId] = useState<string | null>(null)
  const [isWorksiteModalOpen, setIsWorksiteModalOpen] = useState(false)

  // Overlay mode state
  const [overlayMode, setOverlayMode] = useState<OverlayMode>('home')

  // Mobile-only UI state
  const [mapMethods, setMapMethods] = useState<TurfMapMethods | null>(null)
  const [isDrawing, setIsDrawing] = useState(false)
  const setupSheet = useDisclosure()
  const turfSheet = useDisclosure()

  // Auto-open the Setup sheet on mobile when no source list is chosen yet.
  useEffect(() => {
    if (isMobile && !selectedListId) setupSheet.onOpen()
  }, [isMobile]) // eslint-disable-line react-hooks/exhaustive-deps

  // Fetch contacts with locations for the selected contact list
  const { data: contactLocations, error: contactLocationsError } = useQuery({
    queryKey: ['lists', selectedListId, 'contacts-with-locations'],
    queryFn: async () => {
      if (!selectedListId) return []
      const response = await contactListApi.getContactsWithLocations(selectedListId)
      return response.data
    },
    enabled: !!selectedListId,
  })

  // Fetch employment data when overlay mode is work or hybrid
  const { data: contactsWithEmployment, error: employmentError } = useContactsWithEmployment(
    selectedListId || undefined,
    overlayMode !== 'home'
  )
  // The overlay is optional — the home map still works — so a toast, not a blocking alert.
  useErrorToast(employmentError, 'Could not load work locations', 'Work locations for this list could not be loaded.')

  const listLoadError = selectedListId && contactLocationsError
    ? extractApiMessage(contactLocationsError, 'Could not load this list’s contacts.')
    : null

  // Enrich contactLocations with work coordinates from employment data
  const enrichedContactLocations = useMemo(() => {
    if (!contactLocations) return []
    if (!contactsWithEmployment || overlayMode === 'home') return contactLocations

    const workLookup = new Map<string, { lat: number; lng: number }>()
    for (const c of contactsWithEmployment) {
      const emp = c.employments?.find(e => e.isPrimary && e.worksiteLatitude != null)
        ?? c.employments?.find(e => e.worksiteLatitude != null)
      if (emp?.worksiteLatitude != null && emp?.worksiteLongitude != null) {
        workLookup.set(c.id, { lat: emp.worksiteLatitude, lng: emp.worksiteLongitude })
      }
    }

    return contactLocations.map(c => {
      const work = workLookup.get(c.id)
      return work ? { ...c, workLatitude: work.lat, workLongitude: work.lng } : c
    })
  }, [contactLocations, contactsWithEmployment, overlayMode])

  // Compute workplace markers and hybrid household markers
  const { workplaceMarkers, hybridHouseholds } = useMemo(() => {
    if (!contactsWithEmployment || overlayMode === 'home') {
      return { workplaceMarkers: undefined, hybridHouseholds: undefined }
    }

    if (overlayMode === 'work') {
      return {
        workplaceMarkers: groupContactsByWorkplace(contactsWithEmployment),
        hybridHouseholds: undefined,
      }
    }

    // Hybrid mode: workplaces for employed, households for unemployed
    const { withWork, withoutWork } = splitByEmploymentStatus(contactsWithEmployment)
    return {
      workplaceMarkers: groupContactsByWorkplace(withWork),
      hybridHouseholds: contactsToHouseholdMarkers(withoutWork),
    }
  }, [contactsWithEmployment, overlayMode])

  const canSubmit = (campaignId || label.trim()) && selectedListId && drawnTurfs.length > 0 && !batchCutMutation.isPending

  // Track unsaved changes
  useEffect(() => {
    setHasUnsavedChanges(drawnTurfs.length > 0 || !!label.trim() || !!description.trim())
  }, [drawnTurfs, label, description])

  // Unsaved changes protection - browser navigation
  useEffect(() => {
    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      if (hasUnsavedChanges) {
        e.preventDefault()
        e.returnValue = ''
      }
    }
    window.addEventListener('beforeunload', handleBeforeUnload)
    return () => window.removeEventListener('beforeunload', handleBeforeUnload)
  }, [hasUnsavedChanges])

  const handleTurfsChange = (turfs: DrawnTurf[]) => {
    setDrawnTurfs(turfs)
  }

  const handleContactClick = (contactId: string) => {
    setSelectedContactId(contactId)
    setIsContactModalOpen(true)
  }

  const handleWorksiteClick = (businessId: string) => {
    setSelectedWorksiteId(businessId)
    setIsWorksiteModalOpen(true)
  }

  const handleCloseContactModal = () => {
    setIsContactModalOpen(false)
    setSelectedContactId(null)
  }

  const handleAutoTurf = async () => {
    if (!selectedListId || targetTurfCount < 1) return

    try {
      const { jobId } = await autoGeneratePreviewMutation.mutateAsync({
        turfMapLabel: label.trim() || 'Auto-Generated',
        sourceContactListId: selectedListId,
        campaignId: campaignId ?? undefined,
        targetTurfCount,
        walkType,
        addressMode: overlayModeToAddressMode(overlayMode),
        refineWithOsrm: false,
      })

      setPreviewJobId(jobId)
    } catch (error) {
      console.error('Auto-turf preview enqueue error:', error)
      toaster.create({
        title: 'Auto-turf failed',
        description: 'An error occurred while generating turf preview.',
        type: 'error',
        duration: 5000,
      })
    }
  }

  // Drives the auto-generate-preview job to completion (WI-41). Branches on `!previewTerminal`
  // alone — NOT `!previewTerminal || !previewJob` — because a 404 (job not owned / not found)
  // reports isTerminal=true with job undefined; guarding on `!job` too would swallow that case
  // and hang the modal's loading state forever. Nothing is invalidated here: PreviewAsync never
  // persists anything server-side, it only returns polygons for the client to load into the
  // in-memory drawing map, so there is no server cache to refresh.
  useEffect(() => {
    if (!previewTerminal) return

    if (previewJob?.status === 'Succeeded') {
      const preview = previewJob.result as AutoTurfPreviewResult
      autoTurfModal.onClose()

      const methods = (window as any).__multiTurfMapMethods
      if (methods?.loadPreviewTurfs) {
        methods.loadPreviewTurfs(preview.turfs)
      }

      toaster.create({
        title: 'Auto-turf preview loaded',
        description: `${preview.turfs.length} turfs generated. Edit polygons as needed, then save.`,
        type: 'success',
        duration: 5000,
      })
    } else {
      toaster.create({
        title: 'Auto-turf failed',
        description: previewJob?.errorMessage ?? 'An error occurred while generating turf preview.',
        type: 'error',
        duration: 5000,
      })
    }
    setPreviewJobId(null)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [previewTerminal, previewJob])

  const handleSave = async () => {
    if (!canSubmit) return

    try {
      const result = await batchCutMutation.mutateAsync({
        turfMapLabel: campaignId ? '' : label.trim(),
        description: campaignId ? undefined : (description.trim() || undefined),
        sourceContactListId: selectedListId,
        walkType,
        addressMode: overlayModeToAddressMode(overlayMode),
        campaignId: campaignId ?? undefined,
        turfs: drawnTurfs.map(t => ({
          label: t.label,
          turfIndex: t.index,
          boundaryGeoJson: JSON.stringify(t.polygon.geometry),
        })),
      })

      setHasUnsavedChanges(false)

      toaster.create({
        title: 'Turf Map created',
        description: `"${result.label}" created with ${result.turfs.length} turfs and ${result.totalContactCount} total contacts.`,
        type: 'success',
        duration: 5000,
      })

      if (campaignId) {
        navigate(`/dashboard/campaigns/${campaignId}`)
      } else {
        navigate(`/dashboard/turf-maps/${result.id}`)
      }
    } catch (error) {
      console.error('Batch cut error:', error)
      toaster.create({
        title: 'Turf cutting failed',
        description: 'An error occurred while creating the turf map.',
        type: 'error',
        duration: 5000,
      })
    }
  }

  const backTarget = campaignId ? `/dashboard/campaigns/${campaignId}` : '/dashboard/campaigns'

  const handleCancel = () => {
    if (hasUnsavedChanges) {
      onOpen()
    } else {
      navigate(backTarget)
    }
  }

  const handleConfirmLeave = () => {
    setHasUnsavedChanges(false)
    onClose()
    navigate(backTarget)
  }

  // Auto-Turf Configuration Modal — shared between mobile and desktop branches (moved, not duplicated).
  const autoTurfModalEl = (
    <ResponsiveModal isOpen={autoTurfModal.open} onClose={autoTurfModal.onClose} size="md">
      <Dialog.Backdrop />
      <Dialog.Positioner>
        <Dialog.Content>
          <Dialog.Header>Auto-Turf Configuration</Dialog.Header>
          <Dialog.CloseTrigger asChild><CloseButton size="sm" /></Dialog.CloseTrigger>
          <Dialog.Body>
            <VStack gap={4} align="stretch">
              <Text fontSize="sm" color="gray.600">
                Automatically divide the contact list into turfs using geographic clustering.
                You can edit the generated polygons afterwards.
              </Text>

              <Field.Root required>
                <Field.Label>Number of Turfs</Field.Label>
                <NumberInput.Root
                  size="sm"
                  min={2}
                  max={50}
                  value={String(targetTurfCount)}
                  onValueChange={(details: NumberInputValueChangeDetails) => setTargetTurfCount(details.valueAsNumber || 2)}
                >
                  <NumberInput.Input />
                  <NumberInput.Control>
                    <NumberInput.IncrementTrigger />
                    <NumberInput.DecrementTrigger />
                  </NumberInput.Control>
                </NumberInput.Root>
              </Field.Root>

              {drawnTurfs.length > 0 && (
                <Alert.Root status="warning" borderRadius="md" fontSize="sm">
                  <Alert.Indicator />
                  This will replace your {drawnTurfs.length} currently drawn {drawnTurfs.length === 1 ? 'turf' : 'turfs'}.
                </Alert.Root>
              )}
            </VStack>
          </Dialog.Body>
          <Dialog.Footer>
            <Button variant="ghost" mr={3} onClick={autoTurfModal.onClose}>
              Cancel
            </Button>
            <Button
              colorPalette="purple"
              onClick={handleAutoTurf}
              loading={isGeneratingPreview}
              loadingText="Generating..."><FiZap />Generate Preview
                        </Button>
          </Dialog.Footer>
        </Dialog.Content>
      </Dialog.Positioner>
    </ResponsiveModal>
  )

  // Unsaved-changes confirmation dialog — shared between mobile and desktop branches
  // (moved, not duplicated). Structure preserved verbatim from the desktop-only original.
  const unsavedDialogEl = (
    <Dialog.Root
      open={open}
      initialFocusEl={() => cancelRef.current}
      role='alertdialog'
      onOpenChange={e => {
        if (!e.open) {
          onClose();
        }
      }}>
      <Portal>

        <Dialog.Backdrop>
          <Dialog.Positioner>
            <Dialog.Content>
              <Dialog.Header fontSize="lg" fontWeight="bold">
                Unsaved Changes
              </Dialog.Header>

              <Dialog.Body>
                You have unsaved turfs. Are you sure you want to leave? All changes will be lost.
              </Dialog.Body>

              <Dialog.Footer>
                <Button ref={cancelRef} onClick={onClose}>
                  Stay
                </Button>
                <Button colorPalette="red" onClick={handleConfirmLeave} ml={3}>
                  Leave
                </Button>
              </Dialog.Footer>
            </Dialog.Content>
          </Dialog.Positioner>
        </Dialog.Backdrop>

      </Portal>
    </Dialog.Root>
  )

  if (isMobile) {
    return (
      <Flex
        direction="column"
        h="100dvh"
        w="100%"
        position="relative"
        overflow="hidden"
      >
        <TurfCutImmersiveBar
          title={label || 'Cut Turf'}
          onBack={handleCancel}
          onSave={handleSave}
          saveDisabled={!canSubmit}
          saving={batchCutMutation.isPending}
        />

        {/* Map area (or empty-state prompt) */}
        <Box flex={1} position="relative" minH={0}>
          {listLoadError ? (
            <VStack gap={4} justify="center" h="100%" px={6} textAlign="center">
              <Alert.Root status="error" borderRadius="md">
                <Alert.Indicator />
                <Alert.Description>{listLoadError}</Alert.Description>
              </Alert.Root>
              <Button colorPalette="brand" onClick={setupSheet.onOpen} minH="44px"><FiSliders />Choose another list</Button>
            </VStack>
          ) : selectedListId && contactLocations ? (
            <MultiTurfDrawingMap
              contacts={enrichedContactLocations}
              onTurfsChange={handleTurfsChange}
              onHouseholdContactClick={handleContactClick}
              workplaces={overlayMode !== 'home' ? workplaceMarkers : undefined}
              householdOverride={overlayMode === 'hybrid' ? hybridHouseholds : overlayMode === 'work' ? [] : undefined}
              onWorkplaceContactClick={handleContactClick}
              onWorksiteClick={handleWorksiteClick}
              height="100%"
              turfMapLabel={label || undefined}
              overlayMode={overlayMode}
              hideToolbar
              touch
              onDrawingModeChange={setIsDrawing}
              onMethodsReady={setMapMethods}
            />
          ) : (
            <VStack gap={4} justify="center" h="100%" px={6} textAlign="center">
              <Text fontWeight="medium">Choose a contact list to start cutting turf.</Text>
              <Button colorPalette="brand" onClick={setupSheet.onOpen} minH="44px"><FiSliders />Choose contacts
                              </Button>
            </VStack>
          )}

          {/* Drawing hint */}
          {isDrawing && (
            <Box
              position="absolute" top={3} left={3} right={3} zIndex={20}
              bg="blackAlpha.800" color="white" px={3} py={2} borderRadius="md" fontSize="sm" textAlign="center"
            >
              Tap to place points. Tap the first point again to finish.
            </Box>
          )}

          {/* FAB stack (bottom-right) */}
          <VStack position="absolute" bottom="calc(1rem + env(safe-area-inset-bottom, 0px))" right={4} zIndex={20} gap={3} align="stretch">
            {isDrawing ? (
              <IconButton
                aria-label="Cancel drawing"
                colorPalette="gray"
                borderRadius="full"
                boxShadow="lg"
                minH="44px"
                minW="44px"
                onClick={() => mapMethods?.stopDrawing()}><FiX /></IconButton>
            ) : (
              <>
                <IconButton
                  aria-label="Auto-Turf"
                  colorPalette="purple"
                  borderRadius="full"
                  boxShadow="lg"
                  minH="44px"
                  minW="44px"
                  disabled={!selectedListId || !mapMethods}
                  onClick={autoTurfModal.onOpen}><FiZap /></IconButton>
                <IconButton
                  aria-label="Draw turf"
                  colorPalette="brand"
                  borderRadius="full"
                  boxShadow="lg"
                  minH="44px"
                  minW="44px"
                  disabled={!selectedListId || !mapMethods}
                  onClick={() => mapMethods?.startDrawing()}><FiEdit3 /></IconButton>
                <IconButton
                  aria-label="Turf list"
                  variant="outline"
                  colorPalette="gray"
                  bg="surface"
                  color="fg"
                  borderRadius="full"
                  boxShadow="lg"
                  minH="44px"
                  minW="44px"
                  onClick={turfSheet.onOpen}><FiList /></IconButton>
                <IconButton
                  aria-label="Turf setup"
                  variant="outline"
                  colorPalette="gray"
                  bg="surface"
                  color="fg"
                  borderRadius="full"
                  boxShadow="lg"
                  minH="44px"
                  minW="44px"
                  onClick={setupSheet.onOpen}><FiSliders /></IconButton>
              </>
            )}
          </VStack>
        </Box>

        {/* Turf-list bottom sheet */}
        <Drawer.Root placement='bottom' open={turfSheet.open} onOpenChange={e => {
          if (!e.open) {
            turfSheet.onClose();
          }
        }}>
          <Portal>

            <Drawer.Backdrop />
            <Drawer.Positioner>
              <Drawer.Content borderTopRadius="xl" maxH="75vh">
                <Drawer.CloseTrigger asChild><CloseButton size="sm" /></Drawer.CloseTrigger>
                <Drawer.Header>Turfs ({drawnTurfs.length})</Drawer.Header>
                <Drawer.Body pb="env(safe-area-inset-bottom, 16px)">
                  <TurfLegendPanel
                    bare
                    turfs={drawnTurfs}
                    methods={mapMethods ?? undefined}
                  />
                </Drawer.Body>
                <Drawer.Footer pb="env(safe-area-inset-bottom, 16px)">
                  <Button
                    colorPalette="brand"
                    w="100%"
                    minH="44px"
                    loading={batchCutMutation.isPending}
                    disabled={!canSubmit}
                    onClick={handleSave}><FiSave />Save Turf Map
                                  </Button>
                </Drawer.Footer>
              </Drawer.Content>
            </Drawer.Positioner>

          </Portal>
        </Drawer.Root>

        {/* Unsaved-changes confirmation dialog */}
        {unsavedDialogEl}

        {/* Setup bottom sheet */}
        <Drawer.Root placement='bottom' open={setupSheet.open} onOpenChange={e => {
          if (!e.open) {
            setupSheet.onClose();
          }
        }}>
          <Portal>

            <Drawer.Backdrop />
            <Drawer.Positioner>
              <Drawer.Content borderTopRadius="xl" maxH="85vh">
                <Drawer.CloseTrigger asChild><CloseButton size="sm" /></Drawer.CloseTrigger>
                <Drawer.Header>Turf Setup</Drawer.Header>
                <Drawer.Body pb="env(safe-area-inset-bottom, 16px)">
                  <VStack gap={4} align="stretch">
                    {!campaignId && (
                      <Field.Root required>
                        <Field.Label>Turf Map Label</Field.Label>
                        <Input
                          placeholder="e.g., Downtown Canvas - Week 1"
                          value={label}
                          onChange={(e) => setLabel(e.target.value)}
                        />
                      </Field.Root>
                    )}
                    <Field.Root required>
                      <Field.Label>Source Contact List</Field.Label>
                      {listsLoading ? (
                        <Spinner size="sm" />
                      ) : (
                        <NativeSelect.Root>
                          <NativeSelect.Field
                            placeholder="Select a contact list"
                            value={selectedListId}
                            onChange={(e) => setSelectedListId(e.target.value)}>
                            {standaloneLists?.map((list) => (
                              <option key={list.id} value={list.id}>
                                {list.label} ({list.memberCount?.toLocaleString() ?? '?'} members)
                              </option>
                            ))}
                          </NativeSelect.Field>
                          <NativeSelect.Indicator />
                        </NativeSelect.Root>
                      )}
                    </Field.Root>
                    <Field.Root>
                      <Field.Label>Walk Type</Field.Label>
                      <NativeSelect.Root>
                        <NativeSelect.Field
                          value={walkType}
                          onChange={(e) => setWalkType(e.target.value as WalkType)}>
                          <option value="Walk">Walk</option>
                          <option value="Drive">Drive</option>
                          <option value="Phone">Phone</option>
                        </NativeSelect.Field>
                        <NativeSelect.Indicator />
                      </NativeSelect.Root>
                    </Field.Root>
                    {selectedListId && (
                      <UnionOnly>
                        <Field.Root>
                          <Field.Label>Overlay</Field.Label>
                          <OverlayModeToggle mode={overlayMode} onChange={setOverlayMode} disabled={!selectedListId} />
                        </Field.Root>
                      </UnionOnly>
                    )}
                  </VStack>
                </Drawer.Body>
                <Drawer.Footer pb="env(safe-area-inset-bottom, 16px)">
                  <Button colorPalette="brand" w="100%" minH="44px" onClick={setupSheet.onClose} disabled={!selectedListId}>
                    Done
                  </Button>
                </Drawer.Footer>
              </Drawer.Content>
            </Drawer.Positioner>

          </Portal>
        </Drawer.Root>

        {/* Contact/worksite detail modals (shared with desktop) */}
        {selectedContactId && (
          <ContactDetailModal contactId={selectedContactId} isOpen={isContactModalOpen} onClose={handleCloseContactModal} />
        )}
        {selectedWorksiteId && (
          <BusinessDetailModal
            worksiteId={selectedWorksiteId}
            isOpen={isWorksiteModalOpen}
            onClose={() => { setIsWorksiteModalOpen(false); setSelectedWorksiteId(null) }}
            onEmployeeClick={handleContactClick}
          />
        )}

        {/* Auto-Turf Configuration Modal */}
        {autoTurfModalEl}
      </Flex>
    );
  }

  return (
    <>
      <Container maxW="container.xl" py={8}>
        <VStack gap={6} align="stretch">
          {/* Header */}
          <HStack>
            <Button variant="ghost" onClick={handleCancel}><FiArrowLeft />{campaignId ? 'Back to Campaign' : 'Back to Campaigns'}</Button>
          </HStack>

          <Box>
            <Heading size="lg">Create Turf Map</Heading>
            <Text color="gray.500" mt={1}>
              Draw polygons on the map or use Auto-Turf to generate turf boundaries automatically.
            </Text>
          </Box>

          {/* Configuration */}
          <Box bg="surface" p={6} borderRadius="lg" shadow="md">
            <VStack gap={4} align="stretch">
              <HStack gap={4} align="start">
                {!campaignId && (
                  <Field.Root required flex={1}>
                    <Field.Label>Turf Map Label</Field.Label>
                    <Input
                      size="sm"
                      placeholder="e.g., Downtown Canvas - Week 1"
                      value={label}
                      onChange={(e) => setLabel(e.target.value)}
                    />
                  </Field.Root>
                )}

                <Field.Root required flex={1}>
                  <Field.Label>Source Contact List</Field.Label>
                  {listsLoading ? (
                    <Spinner size="sm" />
                  ) : (
                    <NativeSelect.Root size="sm">
                      <NativeSelect.Field
                        placeholder="Select a contact list"
                        value={selectedListId}
                        onChange={(e) => setSelectedListId(e.target.value)}>
                        {standaloneLists?.map(list => (
                          <option key={list.id} value={list.id}>
                            {list.label} ({list.memberCount?.toLocaleString() ?? '?'} members)
                          </option>
                        ))}
                      </NativeSelect.Field>
                      <NativeSelect.Indicator />
                    </NativeSelect.Root>
                  )}
                </Field.Root>

                <Field.Root w="160px">
                  <Field.Label>Walk Type</Field.Label>
                  <NativeSelect.Root size="sm">
                    <NativeSelect.Field
                      value={walkType}
                      onChange={(e) => setWalkType(e.target.value as WalkType)}>
                      <option value="Walk">Walk</option>
                      <option value="Drive">Drive</option>
                      <option value="Phone">Phone</option>
                    </NativeSelect.Field>
                    <NativeSelect.Indicator />
                  </NativeSelect.Root>
                </Field.Root>
              </HStack>

              {!campaignId && (
                <Field.Root>
                  <Field.Label>Description</Field.Label>
                  <Textarea
                    size="sm"
                    placeholder="Optional description for this turf map"
                    rows={2}
                    value={description}
                    onChange={(e) => setDescription(e.target.value)}
                  />
                </Field.Root>
              )}

              <HStack>
                {drawnTurfs.length > 0 && (
                  <Badge bg="info.subtle" color="info.fg" fontSize="sm">
                    {drawnTurfs.length} {drawnTurfs.length === 1 ? 'turf' : 'turfs'} drawn
                  </Badge>
                )}
              </HStack>
            </VStack>
          </Box>

          {/* Map */}
          <Box bg="surface" p={6} borderRadius="lg" shadow="md" position="relative">
            <VStack gap={3} align="stretch">
              <HStack justify="space-between">
                <HStack gap={4}>
                  <Text fontWeight="medium">
                    Draw Turf Boundaries
                  </Text>
                  {selectedListId && (
                    <OverlayModeToggle
                      mode={overlayMode}
                      onChange={setOverlayMode}
                      disabled={!selectedListId}
                    />
                  )}
                </HStack>
                {selectedListId && (
                  <Button
                    size="sm"
                    colorPalette="purple"
                    variant="outline"
                    onClick={autoTurfModal.onOpen}><FiZap />Auto-Turf
                                      </Button>
                )}
              </HStack>

              {!selectedListId && (
                <Alert.Root status="info" borderRadius="md">
                  <Alert.Indicator />
                  Select a source contact list above, then draw polygons on the map or use Auto-Turf.
                </Alert.Root>
              )}

              {listLoadError && (
                <Alert.Root status="error" borderRadius="md">
                  <Alert.Indicator />
                  <Alert.Description>{listLoadError}</Alert.Description>
                </Alert.Root>
              )}

              {selectedListId && contactLocations && (
                <Box position="relative">
                  <MultiTurfDrawingMap
                    contacts={enrichedContactLocations}
                    onTurfsChange={handleTurfsChange}
                    onHouseholdContactClick={handleContactClick}
                    workplaces={overlayMode !== 'home' ? workplaceMarkers : undefined}
                    householdOverride={overlayMode === 'hybrid' ? hybridHouseholds : overlayMode === 'work' ? [] : undefined}
                    onWorkplaceContactClick={handleContactClick}
                    onWorksiteClick={handleWorksiteClick}
                    height="600px"
                    turfMapLabel={label || undefined}
                    overlayMode={overlayMode}
                  />
                  <TurfLegendPanel turfs={drawnTurfs} />
                </Box>
              )}
            </VStack>
          </Box>

          {/* Action Bar */}
          <HStack justify="flex-end" gap={3}>
            <Button variant="outline" onClick={handleCancel}>
              Cancel
            </Button>
            <Button
              colorPalette="brand"
              loading={batchCutMutation.isPending}
              disabled={!canSubmit}
              onClick={handleSave}><FiSave />Save Turf Map
                          </Button>
          </HStack>
        </VStack>
      </Container>

      {/* Unsaved changes confirmation dialog */}
      {unsavedDialogEl}

      {/* Auto-Turf Configuration Modal */}
      {autoTurfModalEl}

      {/* Contact Detail Modal */}
      {selectedContactId && (
        <ContactDetailModal
          contactId={selectedContactId}
          isOpen={isContactModalOpen}
          onClose={handleCloseContactModal}
        />
      )}

      {/* Business Detail Modal */}
      {selectedWorksiteId && (
        <BusinessDetailModal
          worksiteId={selectedWorksiteId}
          isOpen={isWorksiteModalOpen}
          onClose={() => {
            setIsWorksiteModalOpen(false)
            setSelectedWorksiteId(null)
          }}
          onEmployeeClick={handleContactClick}
        />
      )}
    </>
  );
}
